
interface SessionConfig {
  idleTimeoutMs: number; // 4 hours instead of 2
  absoluteTimeoutMs: number; // 24 hours instead of 12
  refreshThresholdMs: number; // Refresh token when 30 minutes left
}

interface SessionData {
  lastActivity: number;
  sessionStart: number;
  tokenExpiry?: number;
}

const SESSION_CONFIG: SessionConfig = {
  idleTimeoutMs: 4 * 60 * 60 * 1000, // 4 hours
  absoluteTimeoutMs: 24 * 60 * 60 * 1000, // 24 hours
  refreshThresholdMs: 30 * 60 * 1000, // 30 minutes
};

const SESSION_STORAGE_KEY = 'session_data';

export const sessionService = {
  // Initialize session when user logs in
  initSession(): void {
    const now = Date.now();
    const sessionData: SessionData = {
      lastActivity: now,
      sessionStart: now,
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    console.log('🔄 Session initialized');
  },

  // Update last activity timestamp
  updateActivity(): void {
    const sessionData = this.getSessionData();
    if (sessionData) {
      sessionData.lastActivity = Date.now();
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    }
  },

  // Get current session data
  getSessionData(): SessionData | null {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;
    
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error('Failed to parse session data:', error);
      return null;
    }
  },

  // Check if session is valid - more lenient for new signups
  isSessionValid(): { valid: boolean; reason?: string } {
    const token = localStorage.getItem('auth_token');
    const userName = localStorage.getItem('userName');
    const userId = localStorage.getItem('userId');
    
    // Primary check: if we have essential auth data, session is valid
    if (token && (userName || userId)) {
      console.log('✅ Session valid - essential auth data present');
      return { valid: true };
    }
    
    // If no token at all, definitely invalid
    if (!token) {
      console.log('❌ Session invalid - no auth token');
      return { valid: false, reason: 'no_token' };
    }
    
    // Check session data for additional validation (but don't require it)
    const sessionData = this.getSessionData();
    if (sessionData) {
      const now = Date.now();
      const timeSinceLastActivity = now - sessionData.lastActivity;
      const timeSinceSessionStart = now - sessionData.sessionStart;

      // Check idle timeout - increased time
      if (timeSinceLastActivity > SESSION_CONFIG.idleTimeoutMs) {
        console.log('⏰ Session expired due to inactivity');
        return { valid: false, reason: 'idle_timeout' };
      }

      // Check absolute timeout - increased time
      if (timeSinceSessionStart > SESSION_CONFIG.absoluteTimeoutMs) {
        console.log('⏰ Session expired due to absolute timeout');
        return { valid: false, reason: 'absolute_timeout' };
      }
    }

    // If we have a token but no session data, still consider valid (for new signups)
    console.log('✅ Session valid - token present (session data may be initializing)');
    return { valid: true };
  },

  // Check if token needs refresh - less aggressive
  shouldRefreshToken(): boolean {
    const sessionData = this.getSessionData();
    if (!sessionData) return false;

    const now = Date.now();
    const timeUntilIdleTimeout = SESSION_CONFIG.idleTimeoutMs - (now - sessionData.lastActivity);
    
    // Only refresh if less than 1 hour left (instead of 30 minutes)
    return timeUntilIdleTimeout < (60 * 60 * 1000);
  },

  // Clear session data
  clearSession(): void {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    console.log('🗑️ Session data cleared');
  },

  // Get time remaining until session expires
  getTimeUntilExpiry(): { idle: number; absolute: number } {
    const sessionData = this.getSessionData();
    if (!sessionData) return { idle: 0, absolute: 0 };

    const now = Date.now();
    const idleTimeRemaining = Math.max(0, SESSION_CONFIG.idleTimeoutMs - (now - sessionData.lastActivity));
    const absoluteTimeRemaining = Math.max(0, SESSION_CONFIG.absoluteTimeoutMs - (now - sessionData.sessionStart));

    return {
      idle: idleTimeRemaining,
      absolute: absoluteTimeRemaining,
    };
  },
};
